import { html, nothing, render } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";
import unsaferHTML from "@escapio/shared/unsaferHTML";
import connect from "@escapio/shared/data-store/connect";
import { removeInitialContent } from "@escapio/shared/components/element-builder";
import {
	STATE_ALTERNATIVES,
	STATE_AVAILABLE,
	STATE_LOADING,
	STATE_WITHOUT_DATES,
	store,
} from "./DataStore";
import { translate } from "@escapio/shared/l10n/translate";
import { data } from "@escapio/shared/state/payload";
import {
	trackClickout,
	trackDirectLink,
} from "@escapio/portal/tracker/Clickout";
import { formatAlternativeDatesAction, formatLabel } from "./tracker";
import {
	formatDates,
	travelData,
	updateDates,
	updateRooms,
} from "@escapio/portal/state/travelData";
import { getFormatter } from "@escapio/shared/l10n/currency";
import { getDiscount } from "../get-discount";
import { getDirectLinkWithTravelData } from "../get-direct-link-with-traveldata";
import {
	formatDateTimeToLocale,
	formatRangeToLocale,
} from "@escapio/shared/l10n/date";
import * as Board from "@escapio/portal/components/board";
import { scrollToCompletelyVisible } from "@escapio/portal/dom/smoothScroll";
import { track } from "@escapio/portal/tracker/EventTracker";
import { trackViewedProductWhenVisible } from "@escapio/portal/tracker/Klaviyo";
import {
	formatEscapioPrice,
	getEscapioDiscount,
	hasEscapioRate,
} from "@escapio/portal/state/escapio-deals-helpers";
import { trackPrefix } from "@escapio/portal/components/escapio-deals/get-track-name";
import "./HotelDirectContactDialogOpener";
import { hasDirectLink } from "@escapio/portal/state/DirectLink";
import "./AvailabilityCalendarInput";
import { openCalendar } from "./calendar-dialog-opener";
import "../escapio-deals/DiscountInfo";
import "./CancelationTooltip";

const { hotel } = data;

const NUM_TWIN_ELEMENTS = 2;
const HAS_DIRECT_LINK = hasDirectLink(hotel);
const BEST_PRICE_INDEX = 0;

// Notice: these suffixes are used in the backend to import these events.
// If you modify these, please take a look at: EscapioDealDailyPerformance.php
const E_DEAL_TRACK_SUFFIX_WITH_PRICE = "Deal mit Preis";
const E_DEAL_TRACK_SUFFIX_WITHOUT_PRICE = "Deal ohne Preis";
const E_DEAL_TRACK_SUFFIX_WITHOUT_DATES = "Deal ohne Datum";

const getClickHandler = (...args) =>
	trackClickout.bind(null, "Hotelprofil", hotel, ...args);

const useExclusiveLayout = state =>
	hotel.exclusive && !state?.hasActiveEscapioRates;

customElements.define(
	"esc-profile-price-list",
	class extends connect(removeInitialContent(HTMLElement), store) {
		update(state) {
			render(this.getTemplate(state), this, { host: this });

			const dealTitle = this.querySelector(".escapio-offer-title");
			if (dealTitle && hasEscapioRate(hotel)) {
				trackViewedProductWhenVisible(dealTitle, hotel.escapio_deal, hotel);
			}
		}

		getTemplate(state) {
			const exclusiveLayout = useExclusiveLayout(state);
			const priceListTemplate = this.getPriceListTemplate(
				state,
				exclusiveLayout,
			);

			if (exclusiveLayout) {
				const offersCount = this.getVisibleOffersCount(state?.prices);
				return html`<details
					class="details price-comparer__price-list-toggle"
					toggable-selector=".price-comparer__price-list"
				>
					<summary class="details__summary">
						<i
							class="price_comparer__price-list-toggle-icon fa fa-balance-scale"
						></i
						><span
							>${translate("hotel_price_comparer_price_list_heading")}
							${offersCount > 0 ? html` (${offersCount})` : nothing}</span
						><i class="details__summary__arrow fa fa-angle-down"></i>
					</summary>
					<div class="price-comparer__price-list">${priceListTemplate}</div>
				</details>`;
			}

			return priceListTemplate;
		}

		getPriceListTemplate(state, exclusiveLayout) {
			if (state.status == STATE_LOADING) {
				return this.getLoadingTemplate();
			}

			if (state.hasActiveEscapioRates) {
				return this.getTemplateForEscapio(
					state.prices?.offers,
					state.status === STATE_WITHOUT_DATES,
					exclusiveLayout,
				);
			}

			switch (state.status) {
				case STATE_AVAILABLE:
					return this.getPricesTemplate(state, exclusiveLayout);
				case STATE_ALTERNATIVES:
					return this.getAlternativesTemplate(state, exclusiveLayout);
				case STATE_WITHOUT_DATES:
					return this.getNoDatesTemplate(exclusiveLayout);
			}

			return HAS_DIRECT_LINK && !exclusiveLayout
				? this.getPricesTemplate(state, exclusiveLayout)
				: html`${this.getErrorTemplate(
						translate("hotel_error_price_comparer_loading_prices_description"),
					)}
					${this.getDirectContactTemplate(exclusiveLayout)}`;
		}

		getLoadingTemplate() {
			// Note: this code is duplicated in the backend template to avoid layout
			// shifts. If you modify this, please take a look at:
			//  resources/templates/portal/hotel/price-comparer.twig
			return html`<div>
				<div class="price-comparer__offer price-comparer__offer--placeholder">
					<div class="content-placeholder">
						<div></div>
						<div></div>
						<div></div>
						<div></div>
						<div></div>
					</div>
				</div>
				<div class="twin-elements">
					<div class="twin-element content-placeholder"></div>
					<div class="twin-element content-placeholder"></div>
				</div>
				<div
					class="price-comparer__offer price-comparer__offer--placeholder mobile-only"
				>
					<div class="content-placeholder">
						<div></div>
						<div></div>
						<div></div>
						<div></div>
						<div></div>
					</div>
				</div>
				<div
					class="price-comparer__offer price-comparer__offer--placeholder mobile-only"
				>
					<div class="content-placeholder">
						<div></div>
						<div></div>
						<div></div>
						<div></div>
						<div></div>
					</div>
				</div>
			</div>`;
		}

		getPricesTemplate({ prices }, exclusiveLayout) {
			const offers = this.getVisibleOffers(prices);
			const [bestOffer, referenceOffer] = offers;
			const referencePrice = referenceOffer ? referenceOffer.total_rate : null;
			const discount = getDiscount(offers);

			const getProviderName = offer => prices.providers[offer.provider].name;

			const elements = offers.map((offer, index) =>
				this.getOfferTemplate(
					offer,
					index,
					getProviderName(offer),
					discount,
					referencePrice,
					exclusiveLayout,
				),
			);

			if (referenceOffer) {
				elements.unshift(
					html`<div class="twin-elements">
						${this.getTwinElementTemplate(
							bestOffer,
							getProviderName(bestOffer),
							0,
							discount,
							referencePrice,
							exclusiveLayout,
						)}
						${this.getTwinElementTemplate(
							referenceOffer,
							getProviderName(referenceOffer),
							1,
							null,
							null,
							exclusiveLayout,
						)}
					</div>`,
				);
			}

			if (HAS_DIRECT_LINK && !exclusiveLayout) {
				elements.unshift(this.getDirectLinkTemplate());
			}

			elements.push(this.getDirectContactTemplate(exclusiveLayout));

			return elements;
		}

		getVisibleOffers(prices) {
			return prices?.hasOffers && Array.isArray(prices.offers)
				? [...prices.offers.slice(0, HAS_DIRECT_LINK ? 2 : 3)]
				: [];
		}

		getVisibleOffersCount(prices) {
			return this.getVisibleOffers(prices).length;
		}

		getTemplateForEscapio(offers, showMinPrice, exclusiveLayout) {
			const discount = getEscapioDiscount(hotel);
			const titleElement = html`<div class="escapio-offer-title">
				${hotel.escapio_deal.title}${
					discount
						? html`<span class="offer__discount"
								>${translate(
									"escapio_deal_discount_text_short",
									discount,
								)}<esc-escapio-deal-discount-info
								></esc-escapio-deal-discount-info
							></span>`
						: nothing
				}
			</div>`;

			const ratesWithOffers = [];
			const offerElements = offers
				? offers
						.filter(
							offer =>
								offer.rate?.id &&
								hotel.escapio_deal.active_rates.hasOwnProperty(offer.rate.id),
						)
						.map(offer => {
							ratesWithOffers.push(`${offer.rate.id}`); // Convert to string because Array.includes used below is strict
							return this.getEscapioOfferTemplate(
								offer.rate.id,
								hotel.escapio_deal.active_rates[offer.rate.id],
								offer,
								false,
							);
						})
				: [];
			const elements = offerElements.concat(
				Object.entries(hotel.escapio_deal.active_rates)
					.toSorted(([, rateA], [, rateB]) => rateA.position - rateB.position)
					.map(([rateId]) => rateId)
					.filter(Boolean)
					.filter(rateId => !ratesWithOffers.includes(rateId))
					.map(rateId =>
						this.getEscapioOfferTemplate(
							rateId,
							hotel.escapio_deal.active_rates[rateId],
							{},
							showMinPrice,
						),
					),
			);

			if (HAS_DIRECT_LINK && !exclusiveLayout) {
				elements.push(
					html`<div class="escapio-offer-edl text-underline">
						<a
							href=${this.directLink}
							target="_blank"
							rel="nofollow noopener"
							@click=${() => {
								trackDirectLink("Hotelprofil", hotel, undefined);
							}}
							>${translate("hotel_escapio_offer_direct_link")}</a
						>
					</div>`,
				);
			}

			return [titleElement, ...elements];
		}

		getTwinElementTemplate(
			offer,
			providerName,
			index,
			discount,
			referencePrice,
			exclusiveLayout,
		) {
			const { total_rate } = offer;

			return html`
				<a
					href=${offer["book_uri"]}
					target="_blank"
					rel="nofollow noopener"
					class="twin-element"
					@click=${getClickHandler(
						offer.provider,
						providerName,
						total_rate,
						index + 1,
					)}
				>
					<div class="twin-element__header">
						${providerName}
						${
							discount
								? html`<span class="offer__discount twin-element__discount"
										>${translate(
											"hotel_list_discount_info_short",
											discount,
										)}</span
									>`
								: nothing
						}
					</div>
					<div class="twin-element__body">
						<div class="twin-element__left-container">
							<div class="offer__room ellipsis">${offer["room_name"]}</div>
							<div class="offer__features">
								${this.getPriceComparerFeatures(offer)}
							</div>
						</div>
						<div class="twin-element__right-container">
							<div class="twin-element__price-container">
								${
									referencePrice && discount
										? html`
												<span class="offer__reference-price"
													>${this.priceFormat(referencePrice)}</span
												>
											`
										: nothing
								}
								<span class="twin-element__offer__price offer__price"
									>${this.priceFormat(offer["total_rate"])}</span
								>
							</div>
							${
								index === BEST_PRICE_INDEX && !exclusiveLayout
									? html` <button
											class="action-button twin-element__cta-button"
										>
											${translate("basic_to_the_offer")}
										</button>`
									: html` <div class="twin-element__see-offer-label">
											${translate("basic_to_the_offer")}
											<span class="fa fa-angle-right fa--space-before"></span>
										</div>`
							}
						</div>
					</div>
				</a>
			`;
		}

		getPriceComparerFeatures(offer) {
			return [
				this.getBoardTemplate(offer.board),
				this.getCancelationTemplate(offer),
			];
		}

		getBoardTemplate(board) {
			if (!Board.isAtLeast(Board.BREAKFAST, board)) {
				return nothing;
			}

			return this.getFeatureTemplate(
				Board.getLabel(board),
				Board.isAtLeast(Board.HALF_BOARD, board)
					? "offer__feature--cutlery"
					: "offer__feature--coffee",
			);
		}

		getCancelationTemplate(offer, useTooltip = false) {
			return offer["has_free_cancelation"]
				? this.getFeatureTemplate(
						offer["free_cancelation_until"]
							? useTooltip
								? html`${translate(
											"basic_free_cancelation",
										)}<esc-hotel-cancelation-tooltip
											info-text=${translate(
												"hotel_escapio_offer_cancelation_tooltip_text",
												formatDateTimeToLocale(
													new Date(offer["free_cancelation_until"]),
												),
											)}
										></esc-hotel-cancelation-tooltip>`
								: translate(
										"basic_free_cancelation_until",
										formatDateTimeToLocale(
											new Date(offer["free_cancelation_until"]),
										),
									)
							: translate("basic_free_cancelation"),
						"offer__feature--money",
					)
				: nothing;
		}

		getFeatureTemplate(text, iconClass) {
			return html`<ul>
				<li class=${iconClass}>${text}</li>
			</ul>`;
		}

		getEscapioOfferTemplate(rateId, rate, offer, showMinPrice) {
			return html`<div class="escapio-offer">
					<div class="escapio-offer-travel-data">
						<div>
							${translate("hotel_page_change_travel_data")}
							<div class="escapio-offer-conditions">
								${translate("hotel_page_offer_min_duration")}
								<strong
									>${rate.min_duration}
									${
										rate.min_duration == 1
											? translate("basic_nights_singular")
											: translate("basic_nights_plural")
									}</strong
								>, ${translate("hotel_page_offer_max_guests")}
								<strong
									>${rate.max_guests_per_room}
									${
										rate.max_guests_per_room == 1
											? translate("basic_guests_singular")
											: translate("basic_guests_plural")
									}</strong
								>
							</div>
						</div>
						<esc-availability-calendar-input
							rate-id=${rateId}
							min-duration=${rate.min_duration}
							max-duration=${rate.max_duration}
						></esc-availability-calendar-input>
						<esc-guest-selector-input
							max-guests=${rate.max_guests_per_room}
							.rooms=${travelData.rooms}
							@confirm=${this.confirmRooms}
						></esc-guest-selector-input>
					</div>
				</div>
				<esc-impression-interaction-tracker
					auto-name
					content-piece="${trackPrefix(hotel.escapio_deal.id, hotel.name)} - ${
						offer?.total_rate
							? E_DEAL_TRACK_SUFFIX_WITH_PRICE
							: showMinPrice
								? E_DEAL_TRACK_SUFFIX_WITHOUT_DATES
								: E_DEAL_TRACK_SUFFIX_WITHOUT_PRICE
					}"
					content-target=${offer?.total_rate ? offer.book_uri : "#"}
					class="escapio-offer-details"
				>
					<div>
						<div class="escapio-offer__room">
							${offer?.room_name || rate.room_name}
						</div>
						<div class="offer__features">
							${[
								this.getBoardTemplate(rate.board),
								this.getCancelationTemplate(offer, true),
								rate.bonus ? unsaferHTML(rate.bonus) : nothing,
							]}
						</div>
					</div>
					<div>
						${
							offer?.total_rate
								? html`<div class="escapio-offer__price">
											${formatEscapioPrice(offer.total_rate)}
										</div>
										<div class="escapio-offer__price-hint">
											${translate("hotel_escapio_offer_price_hint")}
										</div>
										<a
											class="action-button"
											href=${offer.book_uri}
											target="_blank"
										>
											${translate("hotel_escapio_offer_book")}
										</a>
										${
											offer["free_cancelation_until"]
												? html`<div>
														${translate(
															"hotel_escapio_offer_free_cancelation_hint",
															formatDateTimeToLocale(
																new Date(offer["free_cancelation_until"]),
															),
														)}
													</div>`
												: nothing
										} `
								: showMinPrice
									? html`<div class="escapio-offer__price">
												<small>${translate("basic_from")}</small>
												${formatEscapioPrice(rate.min_price)}
											</div>
											<div class="escapio-offer__price-hint">
												${translate("hotel_escapio_offer_price_hint_min_price")}
											</div>
											<button
												class="action-button"
												@click=${() => this.triggerAvailabilityCalendar(rateId)}
											>
												${translate("hotel_page_offer_check_availability")}
											</button>`
									: html`<div class="escapio-offer__unavailable">
												${translate("hotel_page_offer_unavailable")}
											</div>
											<button
												class="action-button"
												@click=${() => this.triggerAvailabilityCalendar(rateId)}
											>
												${translate("hotel_page_offer_change_dates")}
											</button>`
						}
					</div>
				</esc-impression-interaction-tracker>`;
		}

		getOfferTemplate(
			offer,
			index,
			providerName,
			discount,
			referencePrice,
			exclusiveLayout,
		) {
			const classes = classMap({
				"mobile-only":
					referencePrice &&
					index < (HAS_DIRECT_LINK ? NUM_TWIN_ELEMENTS + 1 : NUM_TWIN_ELEMENTS),
			});
			const { total_rate } = offer;

			return html`
				<div class=${classes}>
					<a
						href=${offer["book_uri"]}
						class="price-comparer__offer"
						target="_blank"
						rel="nofollow noopener"
						@click=${getClickHandler(
							offer.provider,
							providerName,
							offer["total_rate"],
							index + 1,
						)}
					>
						<div class="offer__left-container">
							<div class="offer__provider-room-container">
								<div class="offer__provider-name">${providerName}</div>
								<div class="offer__room ellipsis">${offer["room_name"]}</div>
							</div>
							<div class="offer__features">
								${this.getPriceComparerFeatures(offer)}
							</div>
						</div>
						<div class="offer__right-container">
							${
								index === BEST_PRICE_INDEX && discount
									? html`
											<div class="offer_discount-container">
												<span class="offer__reference-price"
													>${this.priceFormat(referencePrice)}</span
												>
												<span class="offer__discount"
													>${translate(
														"hotel_list_discount_info_short",
														discount,
													)}</span
												>
											</div>
										`
									: nothing
							}
							<div class="offer__price">
								${
									index === BEST_PRICE_INDEX && !exclusiveLayout
										? html`
												<button class="action-button">
													${this.priceFormat(total_rate)}
												</button>
											`
										: html`${this.priceFormat(total_rate)}<span
													class="fa fa-angle-right fa--space-before"
												></span>`
								}
							</div>
						</div>
					</a>
				</div>
			`;
		}

		getAlternativesTemplate({ alternatives, filters }, exclusiveLayout) {
			return html`${HAS_DIRECT_LINK && !exclusiveLayout ? this.getDirectLinkTemplate() : nothing}
				${this.getErrorTemplate(
					translate("hotel_error_price_comparer_loading_prices_description"),
				)}
				<div class="price-comparer__alternatives-title">
					${translate("hotel_alternative_travel_periods")}
				</div>
				${alternatives.map(
					({ arrival, departure, bestPrice }) =>
						html`<esc-impression-interaction-tracker
							content-name="Preisvergleich Alternativer Zeitraum"
							content-piece=${formatAlternativeDatesAction(hotel, arrival)}
							content-target=${formatLabel(
								formatDates(travelData.arrival, travelData.departure),
								filters,
							)}
						>
							<a
								href="#"
								class="price-comparer__offer"
								@click=${this.onAlternativeClick(arrival, departure, filters)}
							>
								<div class="offer__left-container offer__alternatives">
									<div class="offer__alternative-period">
										<strong>${formatRangeToLocale(arrival, departure)}</strong>
									</div>
									<div class="offer__alternative-period-price">
										${translate("basic_from")}
										<strong>${this.priceFormat(bestPrice)}</strong>
									</div>
								</div>
								<div class="offer__right-container">
									<button class="action-button">
										${translate("hotel_alternative_travel_period_cta")}
									</button>
								</div>
							</a>
						</esc-impression-interaction-tracker>`,
				)}
				${this.getDirectContactTemplate(exclusiveLayout)}`;
		}

		onAlternativeClick(arrival, departure) {
			return event => {
				event.preventDefault();
				updateDates(arrival, departure);
			};
		}

		get directLink() {
			return HAS_DIRECT_LINK
				? getDirectLinkWithTravelData(hotel.direct_link)
				: null;
		}

		getDirectLinkTemplate() {
			return html`
				<a
					class="price-comparer__offer"
					href=${this.directLink}
					target="_blank"
					rel="nofollow noopener"
					@click=${() => {
						trackDirectLink("Hotelprofil", hotel, undefined);
					}}
				>
					<div class="offer__left-container">
						<div class="offer__provider-room-container">
							<div class="offer__provider-name">
								${translate("hotel_direct_link_book")}
							</div>
							<div class="offer__room ellipsis">${hotel.name}</div>
						</div>
						<div class="offer__teaser">
							${translate("hotel_direct_link_rates_and_offers")}
						</div>
					</div>
					<div
						class="offer__right-container offer__right-container--direct-link"
					>
						<div>
							${translate("hotel_direct_link_website")}<span
								class="fa fa-angle-right fa--space-before"
							></span>
						</div>
					</div>
				</a>
			`;
		}

		getDirectContactTemplate(exclusiveLayout) {
			return data.show_hotel_direct_contact && !exclusiveLayout
				? html`
						<esc-hotel-direct-contact-dialog-opener
							hotel-id=${hotel.id}
							hotel-name=${hotel.name}
							endpoint=${this.getAttribute("hotel-direct-contact-endpoint")}
						></esc-hotel-direct-contact-dialog-opener>
					`
				: nothing;
		}

		getNoDatesTemplate(exclusiveLayout) {
			return html`<div class="price-comparer__offer">
				<div class="offer__left-container"></div>
				<div class="offer__right-container offer__right-container--direct-link">
					<button class="action-button" @click=${this.triggerCalendar}
						>${translate("hotel_check_availability")}</a
					>
				</div>
			</div>
			${HAS_DIRECT_LINK && !exclusiveLayout ? this.getDirectLinkTemplate() : nothing}
			${this.getDirectContactTemplate(exclusiveLayout)}
			`;
		}

		getErrorTemplate(message) {
			return html`
				<div class="message message--colored message--top-space">
					<span class="fa fa-info bullit bullit--blue message__icon"></span>
					<div>${message}</div>
				</div>
			`;
		}

		async triggerCalendar() {
			track("Reisedaten", "Verfügbarkeit prüfen", "Profil");
			await scrollToCompletelyVisible(
				document.querySelector(".price-comparer"),
			);
			openCalendar();
		}

		triggerAvailabilityCalendar(rateId) {
			track("Reisedaten", "Verfügbarkeit prüfen", "Profil");
			const calendarInput = document.querySelector(
				`esc-availability-calendar-input[rate-id="${rateId}"] > esc-calendar-input`,
			);
			calendarInput.showDialog();
		}

		get priceFormat() {
			return getFormatter(travelData.currency);
		}

		confirmDates({ detail }) {
			updateDates(detail.arrival, detail.departure);
		}

		confirmRooms({ detail }) {
			updateRooms(detail.rooms);
		}
	},
);
